// ============================================================================
// ui/shell/tool-cluster.js — top-right tool button cluster (reusable shell)
// ============================================================================
// Ports the Gym Designer's `.tool-controls` (dark): a small top-right group of
// 40×40 icon buttons. Three button kinds:
//   • tool   — participates in a single-active-tool model (e.g. select / hand);
//              choosing one lights it and clears the others → onToolChange(id).
//   • action — momentary (e.g. undo / redo); fires onClick, never stays active.
//   • popover-toggle — a button the host wires to reveal a popover (e.g. zoom);
//              the host toggles its `.is-active` and mounts the popover via mount().
//
//   const cluster = createToolCluster({ onToolChange });
//   cluster.addTool({ id:'select', title:'Select', icon: ICON });
//   const undoBtn = cluster.addAction({ id:'undo', title:'Undo', icon, onClick });
//   const zoomBtn = cluster.addPopoverButton({ id:'zoom', title:'Zoom', icon });
//   cluster.mount(zoomPopover.el);
// ============================================================================

import { h } from '../dom.js';

/**
 * @param {object} [config]
 * @param {(toolId:string)=>void} [config.onToolChange] - fired when an active-tool button is chosen.
 * @param {string} [config.activeTool]                  - initial active tool id.
 * @param {string} [config.ariaLabel]
 */
export function createToolCluster(config = {}) {
  const { onToolChange, activeTool = null, ariaLabel = 'View tools' } = config;

  const el = h('div', { class: 'ds-tool-controls', role: 'toolbar', 'aria-label': ariaLabel });
  const toolButtons = new Map(); // id -> btn (active-tool members only)
  const allBtns = []; // every button, in DOM order — for the roving tabindex
  let current = activeTool;

  // ARIA toolbar = ONE Tab stop + arrow-key navigation between buttons (WD-37).
  el.addEventListener('keydown', onToolbarKeydown);
  function focusable() {
    return allBtns.filter((b) => !b.disabled && b.offsetParent !== null);
  }
  function onToolbarKeydown(e) {
    const list = focusable();
    const idx = list.indexOf(document.activeElement);
    if (idx < 0) return;
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % list.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + list.length) % list.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = list.length - 1;
    if (next == null) return;
    e.preventDefault();
    allBtns.forEach((b) => { b.tabIndex = -1; });
    list[next].tabIndex = 0;
    list[next].focus();
  }

  function makeBtn({ id, title, icon }, extraClass) {
    const btn = h(
      'button',
      {
        type: 'button',
        class: 'ds-tool-btn wd-flat-glass' + (extraClass ? ' ' + extraClass : ''),
        'data-tool': id,
        'aria-label': title,
        title,
        // First button is the single Tab stop; the rest are reachable via arrows.
        tabindex: allBtns.length === 0 ? '0' : '-1',
      },
      h('span', { 'aria-hidden': 'true', html: icon }),
    );
    allBtns.push(btn);
    return btn;
  }

  /** Active-tool member (select / hand). */
  function addTool(spec) {
    const btn = makeBtn(spec);
    btn.setAttribute('aria-pressed', 'false');
    btn.addEventListener('click', () => setActiveTool(spec.id));
    toolButtons.set(spec.id, btn);
    el.appendChild(btn);
    if (current === spec.id) reflectActive();
    return btn;
  }

  /** Momentary action (undo / redo) — never keeps `.is-active`. */
  function addAction(spec) {
    const btn = makeBtn(spec);
    if (typeof spec.onClick === 'function') btn.addEventListener('click', spec.onClick);
    if (spec.disabled) btn.disabled = true;
    el.appendChild(btn);
    return btn;
  }

  /** A toggle button the host wires to a popover (zoom). Returns the button. */
  function addPopoverButton(spec) {
    const btn = makeBtn(spec);
    btn.setAttribute('aria-haspopup', 'true');
    btn.setAttribute('aria-expanded', 'false');
    el.appendChild(btn);
    return btn;
  }

  // When a pop-out popover (zoom) is pinned it "takes over as the blue button"
  // like the GLD — so the tool highlight is visually suppressed while it's open
  // (the tool stays SELECTED underneath; only its blue defers). aria-pressed
  // keeps reflecting the true selection.
  let activeSuppressed = false;
  function reflectActive() {
    toolButtons.forEach((btn, id) => {
      const selected = id === current;
      btn.classList.toggle('is-active', selected && !activeSuppressed);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }
  /** Hide/show the active-tool blue without changing which tool is current. */
  function setActiveSuppressed(v) {
    activeSuppressed = !!v;
    reflectActive();
  }

  function setActiveTool(id) {
    if (!toolButtons.has(id)) return;
    current = id;
    reflectActive();
    if (typeof onToolChange === 'function') onToolChange(id);
  }

  /** Append an element (e.g. a popover) into the cluster's positioning context. */
  function mount(node) {
    el.appendChild(node);
  }

  return {
    el,
    addTool,
    addAction,
    addPopoverButton,
    setActiveTool,
    setActiveSuppressed,
    getActiveTool: () => current,
    mount,
  };
}
